// React-Query-friendly HTTP shim. Each call resolves the Kinde
// access token (refreshing via re-login if needed) before forwarding
// to ``postJson``. All API hooks call through this so the
// ``Authorization: Bearer …`` header is consistent across the app.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback } from "react";

import { getAuthTokenBlocking } from "../auth/getAuthTokenBlocking";
import { postJson } from "./http/postJson";
import { useInrangeAgentsApiConfig } from "./use-config";

export type AgentsHttp = <TResp>(
  path: string,
  body?: unknown,
) => Promise<TResp>;

export const useAgentsHttp = (): AgentsHttp => {
  const { baseUrl } = useInrangeAgentsApiConfig();
  const { getToken } = useKindeAuth();

  return useCallback(
    async <TResp>(path: string, body?: unknown): Promise<TResp> => {
      // No ``relogin`` available here yet - admin-app has a separate
      // ``ReloginContext`` provided by ``building-manager-api-client``
      // that surfaces a re-handshake button on 401. We pass
      // ``undefined`` so the single retry inside ``getAuthTokenBlocking``
      // is skipped; first-time ``getToken()`` failure surfaces as a
      // ``NO_TOKEN`` error the caller can handle.
      const token = await getAuthTokenBlocking(getToken, undefined);
      return postJson<TResp>(baseUrl, path, body, token);
    },
    [baseUrl, getToken],
  );
};
